import { useLayoutEffect, useRef } from 'react';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import {
    useGridApiContext,
    type GridRenderEditCellParams
} from '../lib/dataGrid';
import { useRecentValueEntry } from './recentValueEntry';
import TabSuggestAutocomplete from './TabSuggestAutocomplete';
import { takeThenHandOn } from './gridTabTakesSuggestion';

interface GridAutocompleteEditCellProps extends GridRenderEditCellParams {
    /** Suggestions offered while typing; free-text entries are still allowed. */
    options: string[];
    /** The heading an option is listed under; absent, the list is flat. */
    groupOf?: (option: string) => string;
    /** The last values of this field, newest first — the F2 entry. */
    recent?: readonly string[];
}

/**
 * Grid edit cell backed by a freeSolo Autocomplete — offers suggestions while
 * still accepting any typed value. Styled borderless to fill the cell; surfaces
 * the column's validation error as a tooltip.
 */
export default function GridAutocompleteEditCell(
    props: GridAutocompleteEditCellProps
) {
    const { id, field, value, hasFocus, options, groupOf, recent } = props;
    const { error } = props as GridAutocompleteEditCellProps & {
        error?: string | null;
    };
    const apiRef = useGridApiContext();
    const inputRef = useRef<HTMLInputElement>(null);

    useLayoutEffect(() => {
        if (hasFocus) {
            inputRef.current?.focus();
        }
    }, [hasFocus]);

    function setValue(next: string) {
        apiRef.current.setEditCellValue({ id, field, value: next });
    }

    // "The one from a moment ago": the suggestions above are alphabetical, this
    // offers the same field by recency (see ./recentValueEntry). No button in a
    // cell — see ValidatedGridEditCell.
    const { onKeyDown, menu } = useRecentValueEntry({
        recent,
        value: String(value ?? ''),
        onPick: setValue
    });

    return (
        <Tooltip
            open={!!error && hasFocus}
            title={error ?? ''}
            arrow
            placement="bottom"
        >
            {/* One child for the Tooltip, and the list rides along inside it —
                a Menu portals itself, so it costs the cell no layout. */}
            <span style={{ display: 'flex', width: '100%' }}>
                <TabSuggestAutocomplete
                    freeSolo
                    fullWidth
                    options={options}
                    groupBy={groupOf}
                    inputValue={(value as string) ?? ''}
                    onInputChange={(_event, next) => setValue(next)}
                    onKeyDown={onKeyDown}
                    onTake={(option, event) =>
                        takeThenHandOn(apiRef, { id, field }, option, event)
                    }
                    sx={{ height: '100%' }}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            inputRef={inputRef}
                            variant="standard"
                            error={!!error}
                            sx={{
                                height: '100%',
                                '& .MuiInputBase-root': {
                                    height: '100%',
                                    fontSize: 'inherit',
                                    paddingLeft: '9px'
                                },
                                '& .MuiInputBase-root::before, & .MuiInputBase-root::after':
                                    {
                                        borderBottom: 'none !important'
                                    }
                            }}
                        />
                    )}
                />
                {menu}
            </span>
        </Tooltip>
    );
}
